import { useCallback } from 'react';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import { space, borderRadius, fontSize } from '@ifixit/primitives';
import Language from './language';
import { CardLink } from './card_dropdown_parts';

const LanguageButton = styled(CardLink)`
   font-size: ${fontSize.md};
   padding: ${({ theme }) => `${theme.linkPaddingBlock} ${theme.linkPaddingInline}`};
   color: ${({ theme }) => theme.textColor};

   &:hover > .iso-code {
      color: ${({ theme }) => theme.isoCode.hover.font};
      background-color: ${({ theme }) => theme.isoCode.hover.background};
      border-color: ${({ theme }) => theme.isoCode.hover.border};
   }
`;

interface LanguageIsoCodeProps {
   $isSelected: boolean;
}

const LanguageIsoCode = styled.div<LanguageIsoCodeProps>`
   display: flex;
   color: ${({ $isSelected, theme }) => ($isSelected ? theme.isoCode.selected.font : theme.mainText)};
   background-color: ${({ $isSelected, theme }) =>
      $isSelected ? theme.isoCode.selected.background : 'transparent'};
   line-height: 13px;
   font-family: var(--font-mono);
   margin-right: ${space[4]};
   padding: 1px ${space[1]};
   border: 1px solid
      ${({ $isSelected, theme }) =>
         $isSelected ? theme.isoCode.selected.border : theme.isoCode.standard.border};
   border-radius: ${borderRadius.sm};
`;

const LanguageName = styled.div`
   display: flex;
   color: ${({ theme }) => theme.mainText};
   line-height: inherit;
`;

interface LanguageCardProps {
   language: Language;
   className?: string;
   onClickLanguage: (langid: string) => void;
}

const LanguageCard: FunctionComponent<LanguageCardProps> = ({
   language,
   onClickLanguage,
   className,
}) => {
   const { isoCode, autoglottonym, isSelected } = language;
   const langid = isoCode.toLowerCase();

   const buttonClicked = useCallback(() => onClickLanguage(langid), [onClickLanguage, langid]);

   return (
      <LanguageButton className={className} data-langid={langid} onClick={buttonClicked}>
         <LanguageIsoCode className="iso-code" $isSelected={isSelected}>
            {isoCode}
         </LanguageIsoCode>
         <LanguageName className="language-name">{autoglottonym}</LanguageName>
      </LanguageButton>
   );
};

export default LanguageCard;
